// Per-client analytics tag injection (GTM, Meta Pixel) + SPA page_view tracking.
// Tag config lives in the client_analytics_tag table (backend) — enabling a tag for a
// client is just a DB row, no code change. Follows the script-injection pattern used in
// Utilities/razorpay.js. History import matches Remote.js (direct file import avoids a
// Routing -> Routes -> component -> here circular dependency).

import { post } from './Remote';
import { history } from '../Routing/History';

// client ids whose tag resolution already ran (or is in-flight)
const ensuredClientIds = new Set();
// client_id -> JSON string of the client's active tags ('' = resolved "no tags")
const tagCache = {};
// GTM containers actually injected this page session
const activeContainerIds = new Set();
// Meta pixel state — fbevents.js is shared across all pixel ids
const activePixelIds = new Set();
let fbeventsInjected = false;
let routeTrackingInitialized = false;

const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/i;
const PIXEL_ID_PATTERN = /^\d{10,20}$/;
const CACHE_PREFIX = 'gat_tags_';

export const pushDataLayerEvent = (obj = {}) => {
  if (window.dataLayer) {
    window.dataLayer.push(obj);
  }
};

export const injectGtm = (containerId) => {
  if (!containerId) return;
  const scriptId = `gtm-${containerId}`;
  if (document.getElementById(scriptId)) return;

  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  // Standard GTM bootstrap data + the initial SPA pageview (route tracking only covers changes)
  if (activeContainerIds.size === 0) {
    window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    pushDataLayerEvent({
      event: 'page_view',
      page_path: location.pathname + location.search,
    });
  }

  const script = document.createElement('script');
  script.async = true;
  script.id = scriptId;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);
  activeContainerIds.add(containerId);
};

export const injectMetaPixel = (pixelId) => {
  if (!pixelId || activePixelIds.has(pixelId)) return;

  if (!window.fbq) {
    // Queue stub (as in the official Meta snippet) so init/track calls buffer until
    // fbevents.js loads, which picks the stub up and drains its queue.
    window.fbq = function () {
      window.fbq.queue.push(arguments);
    };
    window.fbq.queue = [];
    window.fbq.loaded = true;
    window.fbq.version = '2.0';

    if (!fbeventsInjected && !document.getElementById('fb-events-js')) {
      const script = document.createElement('script');
      script.async = true;
      script.id = 'fb-events-js';
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(script);
      fbeventsInjected = true;
    }
  }
  // Meta fires the automatic PageView on script load, but as an SPA we drive PageView
  // ourselves (route tracking below) — same model as the GTM containers.
  window.fbq('init', pixelId);
  window.fbq('track', 'PageView');
  activePixelIds.add(pixelId);
};

const readCachedTags = (clientId) => {
  if (Object.prototype.hasOwnProperty.call(tagCache, clientId)) {
    return tagCache[clientId];
  }
  try {
    const cached = sessionStorage.getItem(`${CACHE_PREFIX}${clientId}`);
    if (cached !== null) {
      tagCache[clientId] = cached;
    }
  } catch (e) {
    // sessionStorage unavailable — the module cache still covers this page session
  }
  return tagCache[clientId];
};

const parseCachedTags = (serialized) => {
  if (!serialized) return [];
  try {
    const tags = JSON.parse(serialized);
    return Array.isArray(tags) ? tags : [];
  } catch (e) {
    return [];
  }
};

const applyClientTags = (tags) => {
  (tags || []).forEach((tag) => {
    if (!tag || !tag.tag_id) return;
    if (tag.tag_type === 'gtm' && GTM_ID_PATTERN.test(tag.tag_id)) {
      injectGtm(tag.tag_id);
    } else if (tag.tag_type === 'meta_pixel' && PIXEL_ID_PATTERN.test(tag.tag_id)) {
      injectMetaPixel(tag.tag_id);
    }
  });
};

/**
 * Resolve the client's active analytics tags and inject them. Fire-and-forget safe:
 * idempotent per client id, and a failed fetch only means no tags for this session.
 */
export const ensureClientTags = async (clientId) => {
  const cid = typeof clientId === 'string' ? +clientId : clientId;
  if (!cid || isNaN(cid) || ensuredClientIds.has(cid)) return;
  ensuredClientIds.add(cid);

  const cached = readCachedTags(cid);
  if (cached !== undefined) {
    applyClientTags(parseCachedTags(cached));
    return;
  }

  const res = await post({ client_id: cid }, '/getAnalyticsTagsOfClient');
  // `post` swallows network errors into undefined — only treat an explicit success as
  // authoritative so a transient failure retries on the next ensure() call.
  if (res && res.success && Array.isArray(res.result)) {
    applyClientTags(res.result);
    const serialized = JSON.stringify(res.result);
    tagCache[cid] = serialized;
    try {
      sessionStorage.setItem(`${CACHE_PREFIX}${cid}`, serialized);
    } catch (e) {
      // ignore cache write failure
    }
  } else {
    ensuredClientIds.delete(cid);
  }
};

const isTagTrackingActive = () => activeContainerIds.size > 0 || activePixelIds.size > 0;

/**
 * Push a page_view on every SPA route change. The listen runs always but stays a no-op
 * until a tag is injected, so untagged clients pay nothing.
 */
export const initRouteTracking = () => {
  if (routeTrackingInitialized) return;
  routeTrackingInitialized = true;
  history.listen((location) => {
    if (!isTagTrackingActive()) return;
    if (activeContainerIds.size) {
      pushDataLayerEvent({
        event: 'page_view',
        page_path: location.pathname + location.search,
      });
    }
    if (activePixelIds.size) {
      pushMetaPixelEvent('PageView');
    }
  });
};

const pushMetaPixelEvent = (eventType) => {
  if (window.fbq) {
    window.fbq('track', eventType);
  }
};